import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

type Classification = "Minor" | "Major" | "Observation" | "Opportunity";

const CLASSIFICATIONS: Array<{ value: Classification; label: string; description: string }> = [
  {
    value: "Major",
    label: "Major",
    description:
      "A significant failure, breakdown of the system, or risk of certificate suspension.",
  },
  {
    value: "Minor",
    label: "Minor",
    description:
      "An isolated lapse against a requirement that does not threaten the system overall.",
  },
  {
    value: "Observation",
    label: "Observation",
    description: "A weakness or improvement opportunity that has not yet caused a nonconformity.",
  },
  {
    value: "Opportunity",
    label: "Opportunity",
    description: "An identified opening for improvement.",
  },
];

export function NonconformityNew() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const requirementUuid = params.get("requirementUuid");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [classification, setClassification] = useState<Classification>("Minor");
  const [raisedDuringAudit, setRaisedDuringAudit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [createdUuid, setCreatedUuid] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors([]);
    try {
      const result = await api<{ uuid: string; reference: string }>("/nonconformities", {
        method: "POST",
        body: {
          title,
          description,
          classification,
          relatedRequirementUuid: requirementUuid,
          raisedDuringAudit,
        },
      });
      setCreatedUuid(result.uuid);
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (createdUuid) {
    return <Navigate to={`/o/${slug}/nonconformities/${createdUuid}`} replace />;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Back
        </button>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Raise a nonconformity
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Record a finding that needs correction. You can attach corrective actions on the next
          page.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label
            htmlFor="nc-title"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Title
          </label>
          <input
            id="nc-title"
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "nc-errors" : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        <div>
          <label
            htmlFor="nc-desc"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Description
          </label>
          <textarea
            id="nc-desc"
            rows={5}
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "nc-errors" : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            placeholder="What was observed? What requirement does it relate to? When and where?"
          />
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Classification
          </legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {CLASSIFICATIONS.map((c) => (
              <label
                key={c.value}
                className={`flex cursor-pointer flex-col rounded-md border p-3 text-sm ${
                  classification === c.value
                    ? "border-brand-500 bg-brand-50 dark:border-brand-500 dark:bg-brand-500/10"
                    : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600"
                }`}
              >
                <input
                  type="radio"
                  name="classification"
                  value={c.value}
                  checked={classification === c.value}
                  onChange={() => setClassification(c.value)}
                  className="sr-only"
                />
                <span className="font-medium text-slate-900 dark:text-slate-100">{c.label}</span>
                <span className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  {c.description}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={raisedDuringAudit}
            onChange={(e) => setRaisedDuringAudit(e.target.checked)}
            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-slate-700 dark:text-slate-200">
            Raised as an audit finding (set this if you are an external auditor recording an
            observation during a site visit)
          </span>
        </label>

        {requirementUuid ? (
          <p className="rounded border border-brand-200 bg-brand-50 p-3 text-xs text-brand-900 dark:border-brand-900/40 dark:bg-brand-500/10 dark:text-brand-100">
            This nonconformity will be linked to the clause you came from.
          </p>
        ) : null}

        {errors.length > 0 ? (
          <div
            id="nc-errors"
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          >
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="list-disc space-y-0.5 pl-4">
                {errors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Raising…" : "Raise nonconformity"}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
